import { Fragment, type ReactElement } from 'react';

import { ExternalSnippetPlacement } from '@common/clients/api';
import { type ResolvedContextData } from '@common/defaults/ContextData';
import { type AvailableSidebarThemeID, type SidebarData, WidgetType } from '@common/types/Sidebar';
import { SidebarSequence } from '@common/types/SidebarSequenceByRoute';
import { ThemeID } from '@common/types/ThemeID';
import { TrackedElement } from '@web/atoms/TrackedElement';
import { ExternalSnippet } from '@web/molecules/ExternalSnippet';
import styles from '@web/organisms/Sidebar/Sidebar.module.scss';
import { CLICK_TRACKING_CONFIGS } from '@web/utils/gtm-tracker/config';
import { ComponentClickTargets } from '@web/utils/gtm-tracker/types/click/base';

import { SideWidget } from '../SideWidgets';
import { PLATFORM_SIDEBAR_WIDGETS } from './Sidebar.data';

const CLICK_TRACKING_CONFIG = CLICK_TRACKING_CONFIGS[ComponentClickTargets.SIDEBAR].SIDEBAR_WIDGET_LINK;

// Some types are grouped together because they render the same widget and we only want to render one of them
// prettier-ignore
const DEFAULT_LAYOUT: WidgetType[][] = [
    [WidgetType.MOST_VIEWED],
    [WidgetType.MOST_READ, WidgetType.MOST_COMMENTED],
    [WidgetType.TRANSFER_RUMOUR],
    [WidgetType.MOST_LIKED_COMMENTS],
];

const LAYOUT_PER_PLATFORM: Partial<Record<AvailableSidebarThemeID, WidgetType[][]>> = {
    [ThemeID.BR]: [[WidgetType.LATEST_PODCASTS], ...DEFAULT_LAYOUT],
    [ThemeID.VI]: [
        [WidgetType.RECENT_NEWS],
        [WidgetType.PRO_NEWS, WidgetType.MOST_READ, WidgetType.MOST_COMMENTED],
        [WidgetType.TRANSFER_RUMOUR],
        [WidgetType.MOST_LIKED_COMMENTS],
        [WidgetType.LATEST_PODCASTS],
    ],
    [ThemeID.VN]: [...DEFAULT_LAYOUT, [WidgetType.LATEST_PODCASTS]],
    [ThemeID.VP]: [...DEFAULT_LAYOUT, [WidgetType.LATEST_PODCASTS]],
    [ThemeID.VPBE]: [
        [WidgetType.MOST_VIEWED],
        [WidgetType.MOST_READ, WidgetType.MOST_COMMENTED],
        [WidgetType.LATEST_PODCASTS],
        [WidgetType.MOST_LIKED_COMMENTS],
    ],
};

const getAdsBySequence = (sequence: SidebarSequence): (ReactElement | ReactElement[])[] => {
    switch (sequence) {
        case SidebarSequence.PREMIUM_HOMEPAGE:
            return [];

        case SidebarSequence.RIGHT_COL_HOMEPAGE:
            return [
                <ExternalSnippet
                    placement={ExternalSnippetPlacement.HOME_SIDEBAR_TOP}
                    key={ExternalSnippetPlacement.HOME_SIDEBAR_TOP}
                    isOptional
                />,
                [
                    <ExternalSnippet
                        placement={ExternalSnippetPlacement.HOME_SIDEBAR_AFTER_SECTION_1}
                        key={ExternalSnippetPlacement.HOME_SIDEBAR_AFTER_SECTION_1}
                        isOptional
                    />,
                    <ExternalSnippet
                        placement={ExternalSnippetPlacement.HOME_SIDEBAR_BEFORE_SECTION_2}
                        key={ExternalSnippetPlacement.HOME_SIDEBAR_BEFORE_SECTION_2}
                        isOptional
                    />,
                ],
                [
                    <ExternalSnippet
                        placement={ExternalSnippetPlacement.HOME_SIDEBAR_AFTER_SECTION_2}
                        key={ExternalSnippetPlacement.HOME_SIDEBAR_AFTER_SECTION_2}
                        isOptional
                    />,
                    <ExternalSnippet
                        placement={ExternalSnippetPlacement.HOME_SIDEBAR_BEFORE_SECTION_3}
                        key={ExternalSnippetPlacement.HOME_SIDEBAR_BEFORE_SECTION_3}
                        isOptional
                    />,
                ],
            ];
        default:
            return [
                <ExternalSnippet
                    placement={ExternalSnippetPlacement.NON_HOME_SIDEBAR_TOP}
                    key={ExternalSnippetPlacement.NON_HOME_SIDEBAR_TOP}
                    isOptional
                />,
                [
                    <ExternalSnippet
                        placement={ExternalSnippetPlacement.NON_HOME_SIDEBAR_AFTER_SECTION_1}
                        key={ExternalSnippetPlacement.NON_HOME_SIDEBAR_AFTER_SECTION_1}
                        isOptional
                    />,
                    <ExternalSnippet
                        placement={ExternalSnippetPlacement.NON_HOME_SIDEBAR_BEFORE_SECTION_2}
                        key={ExternalSnippetPlacement.NON_HOME_SIDEBAR_BEFORE_SECTION_2}
                        isOptional
                    />,
                ],
                [
                    <ExternalSnippet
                        placement={ExternalSnippetPlacement.NON_HOME_SIDEBAR_AFTER_SECTION_2}
                        key={ExternalSnippetPlacement.NON_HOME_SIDEBAR_AFTER_SECTION_2}
                        isOptional
                    />,
                    <ExternalSnippet
                        placement={ExternalSnippetPlacement.NON_HOME_SIDEBAR_BEFORE_SECTION_3}
                        key={ExternalSnippetPlacement.NON_HOME_SIDEBAR_BEFORE_SECTION_3}
                        isOptional
                    />,
                ],
            ];
    }
};

const filterWidgetsWithData = (
    platformLayout: WidgetType[][],
    sequenceWidgets: WidgetType[],
    sidebarProps: SidebarData,
) => {
    return platformLayout.filter((widgetTypes) =>
        widgetTypes.some((widgetType) => {
            const widgetData = sidebarProps[widgetType];
            const isWidgetWithData = Array.isArray(widgetData)
                ? widgetData.length > 0
                : typeof widgetData === 'object' && widgetData !== null;
            return sequenceWidgets.includes(widgetType) && isWidgetWithData;
        }),
    );
};

export interface Props {
    contextData: ResolvedContextData;
    injections?: React.ReactNode[];
    adIndex?: number;
    direction?: 'left' | 'right';
}

export const Sidebar = ({ contextData, adIndex, injections, ...props }: Props) => {
    const { sidebarProps, themeID } = contextData;
    const alignmentClass = props.direction === 'left' ? styles['left-side'] : styles['right-side'];

    if (!sidebarProps || themeID === ThemeID.ADMIN) {
        return null;
    }

    const platformLayout = LAYOUT_PER_PLATFORM[themeID] || DEFAULT_LAYOUT;
    const sequenceWidgets = PLATFORM_SIDEBAR_WIDGETS[themeID][sidebarProps.sequence];
    const widgetsWithData = filterWidgetsWithData(platformLayout, sequenceWidgets || [], sidebarProps);

    const Ads = getAdsBySequence(sidebarProps.sequence);
    const totalWidgets = widgetsWithData.length;

    const injectionsToInject = injections?.length ? [...injections] : [];

    // Regular function instead of component to prevent hydration mismatches because the Ads array
    // is being mutated and the rendering order can be different between client and server.
    const getInjectionOverflow = (key: string) => {
        return (
            <Fragment key={key}>
                {themeID === ThemeID.VI && adIndex ? Ads.slice(1) : Ads.shift()}
                {injectionsToInject?.map((Injection, index) => (
                    <Fragment key={`InjectionsOverflow-${index}`}>
                        {Injection}
                        {Ads.shift()}
                    </Fragment>
                ))}
            </Fragment>
        );
    };

    return (
        <TrackedElement
            as="aside"
            className={`${styles.Sidebar} ${alignmentClass} sidebar no-match-ticker`}
            clickTrackingConfig={CLICK_TRACKING_CONFIG}
        >
            {!widgetsWithData.length ? (
                !injectionsToInject.length ? (
                    <>{Ads.shift()}</>
                ) : (
                    getInjectionOverflow('first')
                )
            ) : (
                widgetsWithData.map((widget: WidgetType[], index: number) => {
                    const isLastWidget = index + 1 === totalWidgets;
                    return (
                        <Fragment key={`widgetsWithData-${index}`}>
                            {injectionsToInject.length ? (
                                <>
                                    {injectionsToInject.shift()}
                                    {Ads.shift()}
                                </>
                            ) : (
                                Ads.shift()
                            )}
                            <SideWidget
                                contextData={contextData}
                                key={widget[0]}
                                widgetType={widget[0]}
                                sidebarProps={sidebarProps}
                                adIndex={adIndex}
                            />
                            {isLastWidget ? getInjectionOverflow('last') : null}
                        </Fragment>
                    );
                })
            )}
        </TrackedElement>
    );
};
